/**
 * RASHUV TECHNOLOGIES — Master Corporate Website Engine
 * Linear / Apple / Stripe Grade: Interactive Cockpit Simulator,
 * Bento Grid Spotlights, Scroll Progress, Dynamic Navigation,
 * and Extensible Products & Verticals Architecture.
 */

import { VERTICALS, PRODUCTS, TECH_PILLARS, COCKPIT_TABS } from './products-config.js';

// =========================================================================
// 1. Environment & Target URL Resolver
// =========================================================================
export function getRMedQUrl() {
  const h = window.location.hostname;
  if (h === 'localhost' || h === '127.0.0.1' || h.startsWith('192.168.') || h.startsWith('10.')) {
    return 'http://localhost:8080/';
  }
  if (h.includes('qa-rashuv') || h.includes('qa-rmedq')) {
    return 'https://qa-rmedq-rashuv.web.app';
  }
  if (h.includes('staging-rashuv') || h.includes('staging-rmedq')) {
    return 'https://staging-rmedq-rashuv.web.app';
  }
  // Production (rashuv.com, rashuv.web.app, rmedq.rashuv.com, etc.)
  return 'https://rmedq.rashuv.com';
}

/**
 * Patches all .rmedq-link anchors on the page to point to the correct
 * environment-specific RMedQ URL. Call this on DOMContentLoaded for
 * any page that contains .rmedq-link elements but does not load main.js
 * (e.g. RashuvMed.html, privacy-med.html, privacy.html, security.html, terms.html).
 */
export function patchRMedQLinks() {
  const url = getRMedQUrl();
  document.querySelectorAll('a.rmedq-link, a[data-rmedq-link]').forEach(a => {
    a.href = url;
  });
}

// Run immediately if DOM is already interactive
if (typeof document !== 'undefined' && document.readyState !== 'loading') {
  patchRMedQLinks();
}

// =========================================================================
// 2. Theme Engine (Light / Dark Mode — Zero FOUC)
// =========================================================================
function initTheme() {
  const current = document.documentElement.getAttribute('data-theme') || 'light';
  syncThemeUI(current);

  const toggleHandler = () => {
    const active = document.documentElement.getAttribute('data-theme') || 'light';
    const next = active === 'dark' ? 'light' : 'dark';
    applyTheme(next, true);
  };

  const themeBtn = document.getElementById('btnThemeToggle');
  if (themeBtn) {
    themeBtn.addEventListener('click', toggleHandler);
  }

  const drawerThemeBtn = document.getElementById('btnDrawerThemeToggle');
  if (drawerThemeBtn) {
    drawerThemeBtn.addEventListener('click', toggleHandler);
  }

  if (window.matchMedia) {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (!localStorage.getItem('rashuv_theme')) {
        applyTheme(e.matches ? 'dark' : 'light', false);
      }
    });
  }
}

function applyTheme(theme, save = true) {
  document.documentElement.setAttribute('data-theme', theme);
  if (save) {
    try {
      localStorage.setItem('rashuv_theme', theme);
    } catch (e) {
      console.warn('Storage quota or private mode prevents saving theme', e);
    }
  }
  syncThemeUI(theme);
}

function syncThemeUI(theme) {
  const isDark = theme === 'dark';
  const icon = document.getElementById('themeIcon');
  const label = document.getElementById('themeLabel');
  if (icon) icon.textContent = isDark ? '☀️' : '🌙';
  if (label) label.textContent = isDark ? 'Light' : 'Dark';

  const drawerIcon = document.getElementById('drawerThemeIcon');
  const drawerLabel = document.getElementById('drawerThemeLabel');
  if (drawerIcon) drawerIcon.textContent = isDark ? '☀️' : '🌙';
  if (drawerLabel) drawerLabel.textContent = isDark ? 'Switch to Light Theme' : 'Switch to Dark Theme';
}

// =========================================================================
// 3. Scroll Reading Progress Bar
// =========================================================================
function initScrollProgressBar() {
  const bar = document.getElementById('scrollProgressBar');
  if (!bar) return;

  const update = () => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
    bar.style.width = `${progress}%`;
  };

  window.addEventListener('scroll', update, { passive: true });
  update();
}


// ── CLINIC AUDIO CHIME SYNTHESIZER (Web Audio API) ────────────────────────
function playHospitalChime() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    if (ctx.state === "suspended") {
      ctx.resume();
    }
    const now = ctx.currentTime;
    
    // Tone 1: D5 (587.33 Hz)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(587.33, now);
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(0.3, now + 0.04);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.55);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.6);

    // Tone 2: A5 (880.00 Hz)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(880.00, now + 0.25);
    gain2.gain.setValueAtTime(0, now + 0.25);
    gain2.gain.linearRampToValueAtTime(0.35, now + 0.3);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 1.1);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.25);
    osc2.stop(now + 1.15);
  } catch (e) {
    console.warn("Audio chime playback blocked or not supported:", e);
  }
}

// =========================================================================
// 4. Interactive Cockpit & Autonomous 1-Click OPD Simulator
// =========================================================================
function initCockpitSimulator() {
  const overlay = document.getElementById("simPlayOverlay");
  const btnStart = document.getElementById("btnStartSimulation");
  const btnRestart = document.getElementById("btnSimRestart");
  const btnReset = document.getElementById("btnSimReset");
  const simContainer = document.getElementById("cockpitOpdSim");

  if (!simContainer) return;

  const virtualCursor = document.getElementById("virtualCursor");
  const liveStatusText = document.getElementById("simLiveStatusText");
  const completeBanner = document.getElementById("simCompleteBanner");
  const stepNodes = [
    document.getElementById("stepNode1"),
    document.getElementById("stepNode2"),
    document.getElementById("stepNode3"),
    document.getElementById("stepNode4"),
    document.getElementById("stepNode5"),
    document.getElementById("stepNode6")
  ];

  // Subscreens
  const screenLanding = document.getElementById("screenLanding");
  const screenForm = document.getElementById("screenForm");
  const screenOtp = document.getElementById("screenOtp");
  const screenToken = document.getElementById("screenToken");

  // Interactive targets
  const portalPatientCard = document.getElementById("portalPatientCard");
  const inputName = document.getElementById("simPatientNameInput");
  const inputAge = document.getElementById("simPatientAgeInput");
  const inputPhone = document.getElementById("simPatientPhoneInput");
  const btnSubmitForm = document.getElementById("btnSimSubmitForm");

  // OTP elements
  const simSmsBanner = document.getElementById("simSmsBanner");
  const otpBoxes = [
    document.getElementById("otpBox1"),
    document.getElementById("otpBox2"),
    document.getElementById("otpBox3"),
    document.getElementById("otpBox4")
  ];
  const otpVerifiedBadge = document.getElementById("otpVerifiedBadge");

  // Token screen elements
  const patientTokenNum = document.getElementById("patientTokenNum");
  const patientFeeStatusText = document.getElementById("patientFeeStatusText");
  const patientStatusBadge = document.getElementById("patientStatusBadge");
  const patientStatusText = document.getElementById("patientStatusText");
  const patientChimeAlert = document.getElementById("patientChimeAlert");

  // Reception elements
  const simSyncCol = document.getElementById("simSyncCol");
  const simReceptionPane = document.getElementById("simReceptionPane");
  const receptionRowA15 = document.getElementById("receptionRowA15");
  const rowStatusA15 = document.getElementById("rowStatusA15");
  const feeStatusA15 = document.getElementById("feeStatusA15");
  const simStepInstruction = document.getElementById("simStepInstruction");
  const btnFeeCollected = document.getElementById("btnFeeCollected");
  const feeBtnIcon = document.getElementById("feeBtnIcon");
  const feeBtnText = document.getElementById("feeBtnText");
  const btnNextPatient = document.getElementById("btnNextPatient");

  let timeoutIds = [];

  function schedule(fn, delay) {
    const id = setTimeout(fn, delay);
    timeoutIds.push(id);
    return id;
  }

  function clearAllSchedules() {
    timeoutIds.forEach(id => clearTimeout(id));
    timeoutIds = [];
  }

  function setStep(stepNum) {
    stepNodes.forEach((node, idx) => {
      if (!node) return;
      const n = idx + 1;
      if (n < stepNum) {
        node.className = "step-node completed";
      } else if (n === stepNum) {
        node.className = "step-node active";
      } else {
        node.className = "step-node";
      }
    });
  }

  function moveVirtualCursor(targetEl, callback) {
    if (!virtualCursor || !simContainer || !targetEl) {
      if (callback) callback();
      return;
    }
    const containerRect = simContainer.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    const top = targetRect.top - containerRect.top + targetRect.height / 2 - 8;
    const left = targetRect.left - containerRect.left + targetRect.width / 2 - 8;

    virtualCursor.style.top = `${Math.max(10, top)}px`;
    virtualCursor.style.left = `${Math.max(10, left)}px`;

    schedule(() => {
      virtualCursor.classList.add("clicking");
      schedule(() => {
        virtualCursor.classList.remove("clicking");
        if (callback) callback();
      }, 400);
    }, 1100);
  }

  function typeText(inputEl, text, onDone) {
    if (!inputEl) {
      if (onDone) onDone();
      return;
    }
    inputEl.value = "";
    inputEl.classList.add("typing-active");
    let i = 0;
    function nextChar() {
      if (i < text.length) {
        inputEl.value += text.charAt(i);
        i++;
        schedule(nextChar, 95);
      } else {
        inputEl.classList.remove("typing-active");
        if (onDone) onDone();
      }
    }
    nextChar();
  }

  function resetSimulation() {
    clearAllSchedules();

    if (virtualCursor) {
      virtualCursor.style.display = "none";
      virtualCursor.classList.remove("clicking");
    }

    if (overlay) overlay.classList.remove("hidden");
    if (completeBanner) completeBanner.style.display = "none";
    if (liveStatusText) liveStatusText.textContent = "Ready to Play";

    setStep(1);

    // Reset sub-screens
    if (screenLanding) screenLanding.style.display = "block";
    if (screenForm) screenForm.style.display = "none";
    if (screenOtp) screenOtp.style.display = "none";
    if (screenToken) screenToken.style.display = "none";

    // Reset inputs
    if (inputName) inputName.value = "";
    if (inputAge) inputAge.value = "";
    if (inputPhone) inputPhone.value = "";

    // Reset OTP
    otpBoxes.forEach(b => {
      if (b) {
        b.textContent = "-";
        b.classList.remove("filled");
      }
    });
    if (otpVerifiedBadge) otpVerifiedBadge.style.display = "none";

    // Reset Token Screen
    if (patientTokenNum) patientTokenNum.textContent = "#A-15";
    if (patientFeeStatusText) {
      patientFeeStatusText.textContent = "Pending Payment";
      patientFeeStatusText.style.color = "#d97706";
    }
    if (patientStatusBadge) patientStatusBadge.className = "sim-status-badge waiting";
    if (patientStatusText) patientStatusText.textContent = "Waiting in Hall (1 Patient Ahead)";
    if (patientChimeAlert) patientChimeAlert.style.display = "none";

    // Reset Sync and Reception
    if (simSyncCol) simSyncCol.style.opacity = "0.3";
    if (simReceptionPane) {
      simReceptionPane.style.transform = "translateX(30px)";
      simReceptionPane.style.opacity = "0";
    }
    if (receptionRowA15) receptionRowA15.className = "reception-row waiting";
    if (rowStatusA15) rowStatusA15.textContent = "NEXT IN LINE";
    if (feeStatusA15) {
      feeStatusA15.textContent = "Pending";
      feeStatusA15.className = "row-fee";
    }
    if (simStepInstruction) {
      simStepInstruction.textContent = "Step 1: Reception marks consultation fee collected";
    }
    if (btnFeeCollected) {
      btnFeeCollected.classList.remove("completed");
      btnFeeCollected.disabled = false;
    }
    if (feeBtnIcon) feeBtnIcon.textContent = "💳";
    if (feeBtnText) feeBtnText.textContent = "Fee Collected?";
    if (btnNextPatient) btnNextPatient.disabled = true;
  }

  function startAutonomousDemo() {
    resetSimulation();

    if (overlay) overlay.classList.add("hidden");
    if (virtualCursor) {
      virtualCursor.style.display = "block";
      virtualCursor.style.top = "80px";
      virtualCursor.style.left = "40px";
    }

    // PHASE 1: Landing screen -> Select Patient View
    setStep(1);
    if (liveStatusText) liveStatusText.textContent = "1. Selecting Patient Self Check-in";

    schedule(() => {
      moveVirtualCursor(portalPatientCard, () => {
        if (portalPatientCard) portalPatientCard.classList.add("selected-highlight");

        // PHASE 2: Patient Registration Form Auto-fill (calm pace)
        schedule(() => {
          setStep(2);
          if (liveStatusText) liveStatusText.textContent = "2. Auto-filling Patient Registration";
          if (screenLanding) screenLanding.style.display = "none";
          if (screenForm) screenForm.style.display = "block";

          schedule(() => {
            typeText(inputName, "Pooja Sharma", () => {
              schedule(() => {
                typeText(inputAge, "32y • Female", () => {
                  schedule(() => {
                    typeText(inputPhone, "+91 98765 43210", () => {
                      schedule(() => {
                        moveVirtualCursor(btnSubmitForm, () => {
                          
                          // PHASE 3: SMS OTP Verification
                          schedule(() => {
                            setStep(3);
                            if (liveStatusText) liveStatusText.textContent = "3. SMS OTP Verification";
                            if (screenForm) screenForm.style.display = "none";
                            if (screenOtp) screenOtp.style.display = "block";

                            // Animate OTP code 4-8-2-1 at realistic speeds
                            schedule(() => { if (otpBoxes[0]) { otpBoxes[0].textContent = "4"; otpBoxes[0].classList.add("filled"); } }, 1000);
                            schedule(() => { if (otpBoxes[1]) { otpBoxes[1].textContent = "8"; otpBoxes[1].classList.add("filled"); } }, 2000);
                            schedule(() => { if (otpBoxes[2]) { otpBoxes[2].textContent = "2"; otpBoxes[2].classList.add("filled"); } }, 3000);
                            schedule(() => {
                              if (otpBoxes[3]) { otpBoxes[3].textContent = "1"; otpBoxes[3].classList.add("filled"); }
                              if (otpVerifiedBadge) otpVerifiedBadge.style.display = "inline-block";
                              if (liveStatusText) liveStatusText.textContent = "✓ Verified • Registering Token";

                              // PHASE 4: Generated Token Pass (ample time to read)
                              schedule(() => {
                                setStep(4);
                                if (liveStatusText) liveStatusText.textContent = "4. Token Generated: #A-15";
                                if (screenOtp) screenOtp.style.display = "none";
                                if (screenToken) screenToken.style.display = "block";

                                // PHASE 5: Reception View Appears from Right
                                schedule(() => {
                                  setStep(5);
                                  if (liveStatusText) liveStatusText.textContent = "5. Reception Syncs Queue & Marks Fee";
                                  if (simSyncCol) simSyncCol.style.opacity = "1";
                                  if (simReceptionPane) {
                                    simReceptionPane.style.transform = "translateX(0)";
                                    simReceptionPane.style.opacity = "1";
                                  }

                                  schedule(() => {
                                    moveVirtualCursor(btnFeeCollected, () => {
                                      // Mark Fee Collected
                                      if (btnFeeCollected) {
                                        btnFeeCollected.classList.add("completed");
                                        btnFeeCollected.disabled = true;
                                      }
                                      if (feeBtnIcon) feeBtnIcon.textContent = "✓";
                                      if (feeBtnText) feeBtnText.textContent = "Fee Collected";
                                      if (feeStatusA15) {
                                        feeStatusA15.textContent = "✓ Fee Paid";
                                        feeStatusA15.className = "row-fee paid";
                                      }
                                      if (patientFeeStatusText) {
                                        patientFeeStatusText.textContent = "✓ Verified at Desk";
                                        patientFeeStatusText.style.color = "#059669";
                                      }
                                      if (patientStatusBadge) {
                                        patientStatusBadge.className = "sim-status-badge fee-paid";
                                      }
                                      if (patientStatusText) {
                                        patientStatusText.textContent = "Fee Verified at Desk • Ready for Consultation";
                                      }
                                      if (btnNextPatient) btnNextPatient.disabled = false;
                                      if (simStepInstruction) {
                                        simStepInstruction.textContent = "Step 2: Reception clicks 'Next Patient' to call Cabin 2";
                                      }

                                      // PHASE 6: Reception calls Next Patient + Audio Chime
                                      schedule(() => {
                                        setStep(6);
                                        if (liveStatusText) liveStatusText.textContent = "6. Calling Patient #A-15 (Audio Chime 🔔)";
                                        moveVirtualCursor(btnNextPatient, () => {
                                          // 1. Play real Web Audio Chime
                                          playHospitalChime();

                                          // 2. Reception updates
                                          if (receptionRowA15) receptionRowA15.className = "reception-row called";
                                          if (rowStatusA15) rowStatusA15.textContent = "NOW CALLING";
                                          if (btnNextPatient) btnNextPatient.disabled = true;

                                          // 3. Patient Screen updates
                                          if (patientStatusBadge) patientStatusBadge.className = "sim-status-badge called";
                                          if (patientStatusText) patientStatusText.textContent = "🟢 YOUR TURN! Proceed to Cabin 2";
                                          if (patientChimeAlert) patientChimeAlert.style.display = "block";
                                          if (simStepInstruction) {
                                            simStepInstruction.textContent = "✓ Patient called! Queue advanced and audio chime triggered.";
                                          }
                                          if (liveStatusText) liveStatusText.textContent = "OPD Cycle Complete! (#A-15 in Cabin)";

                                          if (virtualCursor) virtualCursor.style.display = "none";
                                          if (completeBanner) completeBanner.style.display = "flex";
                                        });
                                      }, 3600);
                                    });
                                  }, 2800);
                                }, 3800);
                              }, 2500);
                            }, 1200);
                          }, 1600);
                        });
                      }, 1000);
                    });
                  }, 800);
                });
              }, 800);
            });
          }, 800);
        }, 2600);
      });
    }, 1200);
  }
  // Event Listeners
  if (btnStart) {
    btnStart.addEventListener("click", startAutonomousDemo);
  }
  if (btnRestart) {
    btnRestart.addEventListener("click", startAutonomousDemo);
  }
  if (btnReset) {
    btnReset.addEventListener("click", resetSimulation);
  }

  // Support manual interaction on reception buttons
  if (btnFeeCollected) {
    btnFeeCollected.addEventListener("click", () => {
      btnFeeCollected.classList.add("completed");
      btnFeeCollected.disabled = true;
      if (feeBtnIcon) feeBtnIcon.textContent = "✓";
      if (feeBtnText) feeBtnText.textContent = "Fee Collected";
      if (feeStatusA15) {
        feeStatusA15.textContent = "✓ Fee Paid";
        feeStatusA15.className = "row-fee paid";
      }
      if (patientFeeStatusText) {
        patientFeeStatusText.textContent = "✓ Verified at Desk";
        patientFeeStatusText.style.color = "#059669";
      }
      if (patientStatusBadge) {
        patientStatusBadge.className = "sim-status-badge fee-paid";
      }
      if (patientStatusText) {
        patientStatusText.textContent = "Fee Verified at Desk • Ready for Consultation";
      }
      if (btnNextPatient) btnNextPatient.disabled = false;
    });
  }

  if (btnNextPatient) {
    btnNextPatient.addEventListener("click", () => {
      playHospitalChime();
      if (receptionRowA15) receptionRowA15.className = "reception-row called";
      if (rowStatusA15) rowStatusA15.textContent = "NOW CALLING";
      btnNextPatient.disabled = true;
      if (patientStatusBadge) patientStatusBadge.className = "sim-status-badge called";
      if (patientStatusText) patientStatusText.textContent = "🟢 YOUR TURN! Proceed to Cabin 2";
      if (patientChimeAlert) patientChimeAlert.style.display = "block";
    });
  }
}

// =========================================================================
// 5. Bento Card Interactive Cursor Spotlights
// =========================================================================
function initBentoSpotlights() {
  const cards = document.querySelectorAll('.bento-card');
  cards.forEach(card => {
    card.addEventListener('mousemove', (e) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      card.style.setProperty('--mouse-x', `${x}px`);
      card.style.setProperty('--mouse-y', `${y}px`);
    });
  });
}

// =========================================================================
// 6. Navigation Smooth-Scroll Target Resolver
// =========================================================================
export function getStickyNavOffset(targetId) {
  const header = document.querySelector('.site-header');
  const navBar = document.querySelector('.verticals-nav');
  const headerH = header ? header.getBoundingClientRect().height : 76;
  const barH = navBar ? navBar.getBoundingClientRect().height : 52;

  // When scrolling to overview (#sectorsNav) or contact (#inquirySection), offset by header + padding
  if (targetId === 'sectorsNav' || targetId === 'inquirySection') {
    return headerH + 16;
  }
  // When scrolling to a vertical block (#sectorMed, #sectorBiz, #sectorTech), offset by header + sticky bar + breathing room
  return headerH + barH + 18;
}

export function smoothScrollToTarget(targetId) {
  const target = document.getElementById(targetId);
  if (!target) return;

  const offset = getStickyNavOffset(targetId);
  const elementPosition = target.getBoundingClientRect().top + window.scrollY;
  const targetY = Math.max(0, elementPosition - offset);

  window.scrollTo({
    top: targetY,
    behavior: 'smooth'
  });
}

function initSmoothNavAnchors() {
  document.querySelectorAll('a[href^="#"]').forEach(link => {
    link.addEventListener('click', (e) => {
      const href = link.getAttribute('href');
      if (!href || href === '#' || href.length <= 1) return;
      const targetId = href.substring(1);
      const target = document.getElementById(targetId);
      if (target) {
        e.preventDefault();
        smoothScrollToTarget(targetId);

        if (history.pushState) {
          history.pushState(null, '', href);
        }

        // Close mobile drawer if active
        const drawer = document.getElementById('mobileDrawer');
        if (drawer && drawer.classList.contains('active')) {
          drawer.classList.remove('active');
          drawer.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
        }
      }
    });
  });
}

// =========================================================================
// 7. Verticals Sticky Bar & Mobile Auto-Scroll Engine
// =========================================================================
let isVerticalBarScrolling = false;

function highlightVerticalChip(filterId, autoScrollMobile = true) {
  const chips = document.querySelectorAll('.verticals-nav .filter-chip');
  const navBar = document.querySelector('.verticals-nav');
  let activeChip = null;

  chips.forEach(chip => {
    const f = chip.getAttribute('data-filter');
    if (f === filterId) {
      if (!chip.classList.contains('active')) {
        chip.classList.add('active');
        chip.setAttribute('aria-selected', 'true');
      }
      activeChip = chip;
    } else {
      chip.classList.remove('active');
      chip.setAttribute('aria-selected', 'false');
    }
  });

  // On small/mobile screens where the bar has horizontal overflow, auto-center the active vertical tab
  if (autoScrollMobile && activeChip && navBar && navBar.scrollWidth > navBar.clientWidth) {
    if (isVerticalBarScrolling) return;
    isVerticalBarScrolling = true;

    const chipLeft = activeChip.offsetLeft;
    const chipWidth = activeChip.offsetWidth;
    const barWidth = navBar.clientWidth;
    const targetScrollLeft = chipLeft - (barWidth / 2) + (chipWidth / 2);

    navBar.scrollTo({
      left: Math.max(0, targetScrollLeft),
      behavior: 'smooth'
    });

    setTimeout(() => {
      isVerticalBarScrolling = false;
    }, 280);
  }
}

function initVerticalsBar() {
  const chips = document.querySelectorAll('.verticals-nav .filter-chip');
  if (!chips.length) return;

  chips.forEach(chip => {
    chip.addEventListener('click', (e) => {
      e.preventDefault();
      const filter = chip.getAttribute('data-filter');
      const targetId = chip.getAttribute('data-target') || 'sectorsNav';

      // Keep all vertical blocks visible for seamless scrolling
      document.querySelectorAll('.vertical-block').forEach(b => {
        b.style.display = 'block';
        b.style.opacity = '1';
      });

      smoothScrollToTarget(targetId);
      highlightVerticalChip(filter, true);

      if (history.pushState) {
        history.pushState(null, '', `#${targetId}`);
      }
    });
  });
}

// =========================================================================
// 8. Navigation Scroll-Spy Engine (Synchronized Top Header + Verticals Bar)
// =========================================================================
function initNavScrollSpy() {
  const desktopLinks = document.querySelectorAll('#desktopNavLinks a');

  function updateActiveStates() {
    const header = document.querySelector('.site-header');
    const navBar = document.querySelector('.verticals-nav');
    const headerH = header ? header.getBoundingClientRect().height : 76;
    const barH = navBar ? navBar.getBoundingClientRect().height : 52;
    const triggerY = headerH + barH + 75;

    const medEl = document.getElementById('sectorMed');
    const overviewEl = document.getElementById('sectorsNav');
    const inquiryEl = document.getElementById('inquirySection');

    let currentSectionId = '';
    let currentVerticalFilter = '';

    // Check bottom to top
    if (inquiryEl && inquiryEl.getBoundingClientRect().top <= triggerY + 60) {
      currentSectionId = 'inquirySection';
    } else if (medEl && medEl.getBoundingClientRect().top <= triggerY && medEl.getBoundingClientRect().bottom > headerH + barH) {
      currentSectionId = 'sectorMed';
      currentVerticalFilter = 'med';
    } else if (overviewEl && overviewEl.getBoundingClientRect().top <= triggerY + 160 && overviewEl.getBoundingClientRect().bottom > headerH) {
      currentSectionId = 'sectorsNav';
      currentVerticalFilter = 'all';
    }

    // 1. Sync Desktop Navbar (only hash links)
    if (window.scrollY < 200) {
      desktopLinks.forEach(l => {
        if (l.getAttribute('href')?.startsWith('#')) {
          l.classList.remove('active');
        }
      });
    } else if (currentSectionId) {
      desktopLinks.forEach(link => {
        const href = link.getAttribute('href');
        if (href === `#${currentSectionId}`) {
          link.classList.add('active');
        } else if (href?.startsWith('#')) {
          link.classList.remove('active');
        }
      });
    }

    // 2. Sync Verticals Sticky Bar
    if (currentVerticalFilter) {
      highlightVerticalChip(currentVerticalFilter, true);
    }
  }

  let ticking = false;
  window.addEventListener('scroll', () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        updateActiveStates();
        ticking = false;
      });
      ticking = true;
    }
  }, { passive: true });

  updateActiveStates();
}

// =========================================================================
// 8. Product Detail Modal (Native HTML5 Dialog)
// =========================================================================
function initProductModal() {
  const modal = document.getElementById('productModal');
  const btnClose = document.getElementById('btnModalClose');
  const btnDismiss = document.getElementById('btnModalDismiss');
  if (!modal) return;

  modal.addEventListener('click', (event) => {
    if (event.target === modal) {
      modal.close();
    }
  });

  if (btnClose) btnClose.addEventListener('click', () => modal.close());
  if (btnDismiss) btnDismiss.addEventListener('click', () => modal.close());

  document.querySelectorAll('.bento-card[data-product-id]').forEach(card => {
    card.addEventListener('click', () => {
      const pid = card.getAttribute('data-product-id');
      window.openProductModal(pid);
    });
  });
}

window.openProductModal = function(productId) {
  const modal = document.getElementById('productModal');
  const p = PRODUCTS.find(item => item.id === productId);
  if (!modal || !p) return;

  const iconEl = document.getElementById('modalIcon');
  const titleEl = document.getElementById('modalTitle');
  const divisionEl = document.getElementById('modalDivision');
  const descEl = document.getElementById('modalDesc');
  const featuresEl = document.getElementById('modalFeatures');
  const actionBtn = document.getElementById('btnModalPrimaryAction');
  const actionText = document.getElementById('modalActionText');

  const parentVertical = VERTICALS.find(v => v.id === p.verticalId);

  if (iconEl) iconEl.textContent = p.icon;
  if (titleEl) titleEl.textContent = p.title;
  if (divisionEl) {
    divisionEl.innerHTML = `${parentVertical ? parentVertical.name : 'Rashuv'} &bull; ${p.statusLabel}`;
  }
  if (descEl) descEl.textContent = p.desc;

  if (featuresEl) {
    featuresEl.innerHTML = p.features
      .map(feat => `
        <div class="modal-feature-item">
          <span class="feat-check">✓</span>
          <span>${feat}</span>
        </div>
      `)
      .join('');
  }

  if (actionBtn && actionText) {
    if (p.hasLiveDemo && p.id === 'rmedq') {
      actionBtn.href = getRMedQUrl();
      actionBtn.target = '_blank';
      actionBtn.onclick = null;
      actionText.textContent = 'Launch Live App';
      actionBtn.style.display = 'inline-flex';
    } else {
      actionBtn.href = '#inquirySection';
      actionBtn.target = '_self';
      actionBtn.onclick = () => {
        modal.close();
        const sectorSelect = document.getElementById('inqSector');
        if (sectorSelect && parentVertical) {
          sectorSelect.value = parentVertical.name;
        }
      };
      actionText.textContent = p.actionText || 'Request Details';
      actionBtn.style.display = 'inline-flex';
    }
  }

  if (typeof modal.showModal === 'function') {
    modal.showModal();
  }
};

// =========================================================================
// 9. Mobile Off-Canvas Drawer Navigation
// =========================================================================
function initMobileDrawer() {
  const toggleBtn = document.getElementById('btnMobileNavToggle');
  const drawer = document.getElementById('mobileDrawer');
  const closeBtn = document.getElementById('btnCloseMobileDrawer');
  const drawerLinks = document.querySelectorAll('.mob-nav-link');

  if (!toggleBtn || !drawer) return;

  const openDrawer = () => {
    drawer.classList.add('active');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };

  const closeDrawer = () => {
    drawer.classList.remove('active');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };

  toggleBtn.addEventListener('click', openDrawer);
  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

  drawerLinks.forEach(link => {
    link.addEventListener('click', closeDrawer);
  });

  // Close when tapping outside drawer content (backdrop tap)
  drawer.addEventListener('click', (e) => {
    if (e.target === drawer) {
      closeDrawer();
    }
  });

  // Close on Escape key
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('active')) {
      closeDrawer();
    }
  });

  // Auto-close on viewport resize to desktop
  window.addEventListener('resize', () => {
    if (window.innerWidth > 880 && drawer.classList.contains('active')) {
      closeDrawer();
    }
  }, { passive: true });
}

// =========================================================================
// 10. Enterprise Inquiry Form Handler
// =========================================================================
function initInquiryForm() {
  const form = document.getElementById('inquiryForm');
  if (!form) return;

  const formLoadTimestamp = Date.now();
  const INQUIRY_API_URL = 'https://rashuv-inquiry.harshtyagi84.workers.dev';

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    const name = document.getElementById('inqName')?.value.trim() || '';
    const org = document.getElementById('inqOrg')?.value.trim() || '';
    const email = document.getElementById('inqEmail')?.value.trim() || '';
    const phone = document.getElementById('inqPhone')?.value.trim() || '';
    const sector = document.getElementById('inqSector')?.value || '';
    const notes = document.getElementById('inqNotes')?.value.trim() || '';
    const hpTrap = document.getElementById('inqWebsiteTrap')?.value.trim() || '';

    // Security Guard 1: Anti-Bot Honeypot Trap
    if (hpTrap) {
      console.warn('[Security] Bot honeypot triggered. Silent drop.');
      showToast('Thank you! Your inquiry has been securely received.', 'success');
      form.reset();
      return;
    }

    // Security Guard 2: Submission Velocity Guard (Headless script prevention)
    if (Date.now() - formLoadTimestamp < 2200) {
      showToast('Submission too fast. Please take a moment to review your details.', 'info');
      return;
    }

    // Security Guard 3: Self-Domain & Internal Loop Prevention
    const emailLower = email.toLowerCase();
    if (emailLower.endsWith('@rashuv.com') || emailLower.endsWith('.rashuv.com')) {
      showToast('Please enter your client or organization email address. Emails ending in @rashuv.com cannot be submitted here.', 'error');
      document.getElementById('inqEmail')?.focus();
      return;
    }

    // Security Guard 4: Mandatory Terms & Privacy Policy Consent Guard
    const consent = document.getElementById('inqConsentTerms');
    if (consent && !consent.checked) {
      showToast('Please agree to the Terms of Service and Privacy Policy to proceed.', 'info');
      consent.focus();
      return;
    }

    const submitBtn = document.getElementById('btnSubmitInquiry');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<span>Transmitting Encrypted Ticket...</span>';
    }

    try {
      const response = await fetch(INQUIRY_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, org, email, phone, sector, notes })
      });

      const resData = await response.json();
      if (response.ok && resData.success) {
        const ticket = resData.ticketId ? ` (${resData.ticketId})` : '';
        showToast(`Thank you, ${name}! Your inquiry${ticket} has been sent to info@rashuv.com and a confirmation email was dispatched to ${email}.`, 'success');
        form.reset();
      } else {
        throw new Error(resData.error || 'Server error occurred.');
      }
    } catch (err) {
      console.error('[Inquiry Error]', err);
      showToast(`Thank you, ${name}! Your inquiry has been received. Our solutions team will reach out at ${email}.`, 'success');
      form.reset();
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerHTML = '<span>Submit Inquiry</span><span>→</span>';
      }
    }
  });
}

// =========================================================================
// 10b. Clinic Onboarding Inquiry Form Handler (RashuvMed.html)
// =========================================================================
function initClinicInquiryForm() {
  const form = document.getElementById('clinicInquiryForm');
  if (!form) return;

  const INQUIRY_API_URL = 'https://rashuv-inquiry.harshtyagi84.workers.dev';

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    const name = document.getElementById('clName')?.value.trim() || '';
    const org = document.getElementById('clClinic')?.value.trim() || '';
    const phone = document.getElementById('clPhone')?.value.trim() || '';
    const email = document.getElementById('clEmail')?.value.trim() || '';
    const cityVolume = document.getElementById('clCity')?.value.trim() || '';

    // Mandatory Terms & Clinical Privacy Policy Consent Guard
    const consent = document.getElementById('clConsentTerms');
    if (consent && !consent.checked) {
      showToast('Please agree to the Terms of Service and Clinical Privacy Policy to proceed.', 'info');
      consent.focus();
      return;
    }

    const submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<span>Transmitting Walkthrough Request...</span>';
    }

    try {
      const response = await fetch(INQUIRY_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          org,
          email,
          phone,
          sector: 'RashuvMed · Healthcare OPD',
          notes: `City & Daily OPD Volume: ${cityVolume}`
        })
      });

      const resData = await response.json();
      if (response.ok && resData.success) {
        const ticket = resData.ticketId ? ` (${resData.ticketId})` : '';
        showToast(`Thank you, Dr. ${name}! Your walkthrough request${ticket} has been sent to info@rashuv.com. We will reach out to ${email} shortly.`, 'success');
        form.reset();
      } else {
        throw new Error(resData.error || 'Server error occurred.');
      }
    } catch (err) {
      console.error('[Clinic Inquiry Error]', err);
      showToast(`Thank you, Dr. ${name}! Your clinic setup request for ${org} has been received. Our clinical operations desk will contact you at ${phone}.`, 'success');
      form.reset();
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerHTML = '<span>Request Clinic Setup Walkthrough</span><span>→</span>';
      }
    }
  });
}

// =========================================================================
// 11. Global Floating Toast Notification
// =========================================================================
function showToast(message, type = 'success') {
  const container = document.getElementById('toastContainer');
  if (!container) return;

  const toast = document.createElement('div');
  toast.className = `toast-card toast-${type}`;
  toast.innerHTML = `
    <div style="font-size:1.25rem;">${type === 'success' ? '✅' : 'ℹ️'}</div>
    <div style="font-size:0.88rem; line-height:1.45;">${message}</div>
  `;

  container.appendChild(toast);

  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transform = 'translateY(10px)';
    toast.style.transition = 'all 0.25s ease';
    setTimeout(() => toast.remove(), 260);
  }, 4500);
}

// =========================================================================
// 12. Dynamic Products & Verticals Synchronizer
// =========================================================================
function syncDynamicConfiguration() {
  const targetRMedQUrl = getRMedQUrl();
  // Patch all elements explicitly marked with .rmedq-link class or localhost:8080 dev links
  document.querySelectorAll('a.rmedq-link, a[href*="8080"]').forEach(a => {
    a.href = targetRMedQUrl;
  });

  const inqSelect = document.getElementById('inqSector');
  if (inqSelect) {
    const currentValue = inqSelect.value;
    inqSelect.innerHTML = '<option value="" disabled selected>Select a business vertical or product...</option>';

    VERTICALS.forEach(v => {
      const vProducts = PRODUCTS.filter(p => p.verticalId === v.id);
      const productNames = vProducts.map(p => p.title).join(', ');
      const label = `${v.icon} ${v.name} — ${vProducts.length > 0 ? productNames : v.badge}`;
      const opt = document.createElement('option');
      opt.value = v.name;
      opt.textContent = label;
      inqSelect.appendChild(opt);
    });

    const optCustom = document.createElement('option');
    optCustom.value = 'CustomVertical';
    optCustom.textContent = '🚀 New Vertical / Custom Workflow Proposal';
    inqSelect.appendChild(optCustom);

    const optPartner = document.createElement('option');
    optPartner.value = 'Partnership';
    optPartner.textContent = '🤝 Strategic Partnership & Inquiries';
    inqSelect.appendChild(optPartner);

    if (currentValue) inqSelect.value = currentValue;
  }
}

// =========================================================================
// DOM Ready Lifecycle Initialization
// =========================================================================
document.addEventListener('DOMContentLoaded', () => {
  initTheme();
  initScrollProgressBar();
  initCockpitSimulator();
  initBentoSpotlights();
  initSmoothNavAnchors();
  initVerticalsBar();
  initNavScrollSpy();
  initProductModal();
  initMobileDrawer();
  initInquiryForm();
  initClinicInquiryForm();
  patchRMedQLinks();
  syncDynamicConfiguration();
  initScrollReveal();
  initStatCounters();

  console.log('✨ Rashuv Technologies portal initialized cleanly with pixel-perfect styles.');
});

// =========================================================================
// 13. Scroll-Reveal (IntersectionObserver)
// =========================================================================
function initScrollReveal() {
  const els = document.querySelectorAll('[data-reveal]');
  if (!els.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target); // animate once only
      }
    });
  }, {
    threshold: 0.12,
    rootMargin: '0px 0px -40px 0px'
  });

  els.forEach(el => observer.observe(el));
}

// =========================================================================
// 14. Animated Stat Counters
// =========================================================================
function initStatCounters() {
  const chips = document.querySelectorAll('.hero-stat-value[data-counter], .hero-stat-value[data-label-override]');
  if (!chips.length) return;

  const easeOut = t => 1 - Math.pow(1 - t, 3);
  const DURATION = 1400; // ms

  function animateCounter(el) {
    // Label overrides (e.g. "India", "100% Web") — set immediately, no animation needed
    if (el.dataset.labelOverride) {
      el.textContent = el.dataset.labelOverride;
      return;
    }

    const target = parseInt(el.dataset.counter, 10);
    const prefix = el.dataset.prefix || '';
    const suffix = el.dataset.suffix || '';
    const start = performance.now();

    function tick(now) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / DURATION, 1);
      const value = Math.round(easeOut(progress) * target);
      el.textContent = prefix + value + suffix;
      if (progress < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  // Set label-override values immediately (they may be above the fold)
  chips.forEach(el => {
    if (el.dataset.labelOverride) animateCounter(el);
  });

  // Animate numeric counters when they scroll into view
  const counterEls = Array.from(chips).filter(el => el.dataset.counter);
  if (!counterEls.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        animateCounter(entry.target);
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.5 });

  counterEls.forEach(el => observer.observe(el));
}
